import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, ArrowDown, ArrowUp, Pencil, X, CaretDown } from "@phosphor-icons/react";
import InventoryItemCombobox from "@/components/InventoryItemCombobox";
import VendorCombobox from "@/components/VendorCombobox";
import BatchCombobox from "@/components/BatchCombobox";
import Pagination from "@/components/Pagination";
import { useAccess } from "@/lib/access";
import DocumentUpload, { documentUrl } from "@/components/inventory/DocumentUpload";

export default function Stock() {
  const { can } = useAccess();
  const [movements, setMovements] = useState([]);
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogType, setDialogType] = useState(null); // "in" | "out" | "adjust"
  const [expandedId, setExpandedId] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const load = async () => {
    setLoading(true);
    try {
      const [mv, it, vd] = await Promise.all([
        api.get("/inventory/movements", { params: { limit: 2000 } }),
        api.get("/inventory/items"),
        api.get("/vendors"),
      ]);
      setMovements(mv.data || []);
      setItems(it.data || []);
      setVendors(vd.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Clamp the current page back into range after a reload (e.g. the movement
  // list shrinks or the page size changes) instead of resetting to page 1.
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(movements.length / pageSize));
    setPage((p) => Math.min(p, totalPages));
  }, [movements, pageSize]);

  const fmtDate = (s) => new Date(s).toLocaleString();
  const pageRows = movements.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div data-testid="inv-stock-page">
      <div className="glass-header sticky top-0 z-10 px-8 py-5">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-600 font-semibold">
              Inventory · Movements
            </div>
            <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Stock Ledger</h1>
            <p className="text-sm text-slate-500 mt-1">
              Every IN, OUT, and adjustment — FIFO/FEFO-aware, batch-tracked.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {can("stock.in") && (
            <button
              onClick={() => setDialogType("in")}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
              data-testid="stock-in-btn"
            >
              <ArrowDown size={14} weight="bold" /> Stock In
            </button>
            )}
            {can("stock.out") && (
            <button
              onClick={() => setDialogType("out")}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-rose-600 text-white text-sm font-medium hover:bg-rose-700"
              data-testid="stock-out-btn"
            >
              <ArrowUp size={14} weight="bold" /> Stock Out
            </button>
            )}
            {can("stock.adjust") && (
            <button
              onClick={() => setDialogType("adjust")}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-slate-200 bg-white text-slate-700 text-sm hover:bg-slate-50"
              data-testid="stock-adjust-btn"
            >
              <Pencil size={14} weight="bold" /> Adjust
            </button>
            )}
          </div>
        </div>
      </div>

      <div className="p-8 space-y-5">
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {["When", "Type", "Item", "Qty", "By", ""].map((h) => (
                  <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!loading && movements.length === 0 && (
                <tr><td colSpan={6} className="px-6 py-12 text-center text-sm text-slate-500">No movements yet. Start with Stock In.</td></tr>
              )}
              {pageRows.map((m) => {
              const open = expandedId === m.id;
              return (
                <React.Fragment key={m.id}>
                  <tr
                    className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                    data-testid={`movement-row-${m.id}`}
                    onClick={() => setExpandedId(open ? null : m.id)}
                  >
                    <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(m.timestamp)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] uppercase tracking-[0.16em] font-semibold px-2 py-1 rounded ${typeTone(m.type)}`}>{m.type}</span>
                      {m.qa_status && QA_LABEL[m.qa_status] && (
                        <span
                          className={`ml-2 text-[10px] uppercase tracking-[0.12em] font-semibold px-2 py-1 rounded ${QA_LABEL[m.qa_status].tone}`}
                          data-testid={`movement-qa-${m.id}`}
                        >
                          {QA_LABEL[m.qa_status].label}
                        </span>
                      )}
                      {REASON_LABEL[m.reason] && (
                        <span className="ml-2 text-[10px] uppercase tracking-[0.12em] font-semibold px-2 py-1 rounded bg-slate-100 text-slate-700" data-testid={`movement-reason-${m.id}`}>
                          {REASON_LABEL[m.reason]}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-slate-900">{m.item_name}</td>
                    <td className="px-4 py-3 text-sm tabular-nums">
                      {m.qty} {m.uom}
                      {m.qa_blocked_qty > 0 && (
                        <div className="text-[11px] text-slate-500">{qaSummary(m, false)}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">{m.created_by_name || m.created_by_email}</td>
                    <td className="px-4 py-3 text-right">
                      <CaretDown
                        size={14}
                        className={`inline text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}
                      />
                    </td>
                  </tr>
                  {open && (
                    <tr className="bg-slate-50/70 border-t border-slate-100" data-testid={`movement-detail-${m.id}`}>
                      <td colSpan={6} className="px-4 py-4">
                        <MovementDetail m={m} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
              })}
            </tbody>
          </table>
          <Pagination
            page={page}
            pageSize={pageSize}
            total={movements.length}
            onPageChange={setPage}
            onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
            testId="stock-pagination"
          />
        </div>
      </div>

      {dialogType && (
        <MovementDialog
          type={dialogType}
          items={items}
          vendors={vendors}
          onClose={() => setDialogType(null)}
          onDone={() => { setDialogType(null); load(); }}
        />
      )}
    </div>
  );
}

const QA_LABEL = {
  pending: { label: "QA Hold", tone: "bg-amber-50 text-amber-700" },
  accepted: { label: "QA Accepted", tone: "bg-emerald-50 text-emerald-700" },
  partial: { label: "QA Partial", tone: "bg-amber-50 text-amber-800" },
  rejected: { label: "QA Rejected", tone: "bg-rose-50 text-rose-700" },
};

// Where a partially/fully rejected receipt ended up, including the NCR's later decision on the blocked part.
function qaSummary(m, withUom = true) {
  const u = withUom ? ` ${m.uom}` : "";
  const acc = Number(m.qa_accepted_qty || 0);
  const blk = Number(m.qa_blocked_qty || 0);
  if (m.qa_disposition === "release") return `${acc + blk}${u} usable (${blk}${u} released after re-check)`;
  if (m.qa_disposition === "return") return `${acc}${u} usable · ${blk}${u} returned to vendor`;
  if (m.qa_disposition === "write_off") return `${acc}${u} usable · ${blk}${u} written off`;
  return `${acc}${u} usable · ${blk}${u} blocked`;
}

// OUT movements raised from an NCR decision on QA-blocked stock.
const REASON_LABEL = {
  qa_return: "Returned to vendor",
  qa_write_off: "QA write-off",
};

function typeTone(t) {
  if (t === "in") return "bg-emerald-50 text-emerald-700";
  if (t === "out") return "bg-rose-50 text-rose-700";
  if (t === "adjust") return "bg-amber-50 text-amber-700";
  return "bg-slate-100 text-slate-600";
}

// Everything trimmed off the main Stock Ledger row (Batch, Vendor, GRN, etc.)
// lives here instead — reachable by clicking the row, not gone.
function MovementDetail({ m }) {
  const rows = [];
  if (m.type === "in") {
    rows.push(["Batch No", m.batch_no || "—"]);
    rows.push(["Vendor", m.vendor_name || "—"]);
    rows.push(["GRN No", m.grn_no || "—"]);
    if (m.invoice_no) rows.push(["Invoice No", m.invoice_no]);
    if (m.invoice_date) rows.push(["Invoice Date", m.invoice_date]);
    if (m.invoice_doc_id) {
      rows.push([
        "Invoice",
        <a
          href={documentUrl(m.invoice_doc_id)}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-emerald-700 hover:underline"
        >
          View invoice ↗
        </a>,
      ]);
    }
    rows.push(["Storage Location", m.to_location || "—"]);
    if (m.unit_cost) rows.push(["Unit Cost", `₹${m.unit_cost}`]);
    if (m.qa_blocked_qty > 0) {
      rows.push(["QA Result", `${m.qa_accepted_qty} ${m.uom} accepted at inspection · ${m.qa_blocked_qty} ${m.uom} blocked`]);
      rows.push(["Blocked Qty Outcome", m.qa_disposition ? qaSummary(m) : "Awaiting NCR decision"]);
    }
    if (m.ncr_code) {
      rows.push([
        "NCR",
        <Link to="/inventory/procurement?tab=ncrs" onClick={(e) => e.stopPropagation()} className="text-emerald-700 hover:underline">
          {m.ncr_code} →
        </Link>,
      ]);
    }
  }
  if (m.type === "adjust") {
    rows.push(["Batch No", m.batch_no || "—"]);
    if (m.previous_qty !== undefined && m.previous_qty !== null) {
      rows.push(["Change", `${m.previous_qty} → ${m.new_qty} ${m.uom}`]);
    }
  }
  if (m.type === "out" && Array.isArray(m.consumed_batches) && m.consumed_batches.length > 0) {
    rows.push([
      "Consumed From",
      m.consumed_batches.map((b) => `${b.batch_no || "—"} (${b.qty})`).join(", "),
    ]);
  }
  if (m.reference) rows.push(["Reference", m.reference]);
  if (m.note) rows.push(["Note", m.note]);

  if (rows.length === 0) {
    return <p className="text-xs text-slate-400">No additional details.</p>;
  }

  return (
    <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3">
      {rows.map(([label, value]) => (
        <div key={label}>
          <dt className="text-[10px] uppercase tracking-wider text-slate-400">{label}</dt>
          <dd className="text-xs text-slate-700 mt-0.5 break-words">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function MovementDialog({ type, items, vendors, onClose, onDone }) {
  const [form, setForm] = useState({
    item_id: "", qty: "", batch_no: "", lot_no: "", expiry_date: "",
    unit_cost: "", vendor_id: "", vendor_name: "", grn_no: "", to_location: "MAIN",
    from_location: "", reference: "", note: "", new_qty: "", current_qty: "",
    invoice_no: "", invoice_date: "",
  });
  const [invoiceDoc, setInvoiceDoc] = useState(null);
  const [batches, setBatches] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const selectedItem = items.find((i) => i.id === form.item_id);
  // Mirrors assert_item_usable on the backend: only approved items can be
  // received; blocked/archived items can't be issued; adjust is always allowed.
  const pickableItems = items.filter((i) => {
    const s = i.status || "approved";
    if (type === "in") return s === "approved";
    if (type === "out") return s !== "blocked" && s !== "archived";
    return true;
  });

  // Adjust only: whenever the item changes, drop any batch picked for the
  // previous item and load the current item's real, available batches —
  // this is what lets the batch dropdown only ever offer batches that exist.
  useEffect(() => {
    if (type !== "adjust") return;
    setForm((f) => ({ ...f, batch_no: "", new_qty: "", current_qty: "" }));
    if (!form.item_id) {
      setBatches([]);
      return;
    }
    let cancelled = false;
    api
      .get("/inventory/stock", { params: { item_id: form.item_id, status: "available" } })
      .then(({ data }) => {
        if (!cancelled) setBatches((data || []).filter((b) => (b.qty || 0) > 0));
      })
      .catch(() => {
        if (!cancelled) setBatches([]);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, form.item_id]);

  const pickVendor = (vendorId) => {
    const v = vendors.find((x) => x.id === vendorId);
    setForm((f) => ({ ...f, vendor_id: vendorId, vendor_name: v?.name || "" }));
  };

  const pickBatch = (batchNo) => {
    const b = batches.find((x) => x.batch_no === batchNo);
    setForm((f) => ({
      ...f,
      batch_no: batchNo,
      current_qty: b ? b.qty : "",
      new_qty: b ? String(b.qty) : "",
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (type === "adjust" && !form.batch_no) {
      toast.error("Select a batch to adjust");
      return;
    }
    if (type === "in") {
      if (!form.vendor_id) return toast.error("Select the vendor");
      if (!invoiceDoc) return toast.error("Upload the invoice document");
    }
    setSubmitting(true);
    try {
      const body = {
        item_id: form.item_id,
        type,
        qty: type === "adjust" ? 0 : Number(form.qty || 0),
        batch_no: form.batch_no,
        lot_no: form.lot_no,
        expiry_date: form.expiry_date,
        unit_cost: Number(form.unit_cost || 0),
        vendor_id: form.vendor_id,
        vendor_name: form.vendor_name,
        grn_no: form.grn_no,
        to_location: form.to_location,
        from_location: form.from_location,
        reference: form.reference,
        note: form.note,
      };
      if (type === "adjust") body.new_qty = Number(form.new_qty || 0);
      if (type === "in") {
        body.invoice_no = form.invoice_no.trim();
        body.invoice_date = form.invoice_date;
        body.invoice_doc_id = invoiceDoc.id;
      }
      const { data } = await api.post("/inventory/stock/movement", body);
      const grnNo = data?.movement?.grn_no;
      const uom = selectedItem?.uom || "";
      if (type === "in" && grnNo) {
        toast.success(`Stock IN recorded — GRN ${grnNo}. On QA hold until QA accepts it.`);
      } else if (type === "adjust" && data?.stock) {
        toast.success(`Batch ${form.batch_no} adjusted: ${data.stock.previous_qty} → ${data.stock.new_qty} ${uom}`);
      } else {
        toast.success(`Stock ${type.toUpperCase()} recorded`);
      }
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  const title = type === "in" ? "Stock In (GRN)" : type === "out" ? "Stock Out / Issue" : "Stock Adjustment";

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center px-4 py-6 overflow-y-auto" data-testid={`movement-dialog-${type}`}>
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl w-full max-w-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="font-heading text-lg font-bold text-slate-900">{title}</h2>
          <button type="button" onClick={onClose} className="p-2 text-slate-500 hover:bg-slate-100 rounded">
            <X size={16} />
          </button>
        </div>
        <div className="px-6 py-5 space-y-3 max-h-[70vh] overflow-y-auto">
          <Field label="Item *">
            <InventoryItemCombobox
              items={pickableItems}
              value={form.item_id}
              onPick={(v) => set("item_id", v)}
              testId="mv-item-select"
              placeholder="Select item…"
            />
          </Field>
          {selectedItem && (
            <p className="text-xs text-slate-500 -mt-1" data-testid="mv-current-stock">
              Current stock:{" "}
              <span className="font-medium text-slate-700">
                {Number(selectedItem.available_qty || 0).toLocaleString("en-IN")} {selectedItem.uom}
              </span>
              {selectedItem.expired_qty > 0 && (
                <span className="text-rose-600">
                  {" "}· {Number(selectedItem.expired_qty).toLocaleString("en-IN")} {selectedItem.uom} expired (not issuable)
                </span>
              )}
            </p>
          )}

          {type === "adjust" ? (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Batch *">
                <BatchCombobox
                  batches={batches}
                  value={form.batch_no}
                  onPick={pickBatch}
                  testId="mv-batch-select"
                  placeholder={form.item_id ? "Select batch…" : "Select an item first"}
                  disabled={!form.item_id}
                />
              </Field>
              <Field label="Current Qty">
                <input
                  readOnly
                  value={form.batch_no ? `${form.current_qty} ${selectedItem?.uom || ""}` : "—"}
                  className={`${inp} bg-slate-50 text-slate-500 cursor-not-allowed`}
                  data-testid="mv-current-qty"
                />
              </Field>
            </div>
          ) : null}

          <div className="grid grid-cols-2 gap-3">
            {type === "adjust" ? (
              <Field label="New Quantity *">
                <input
                  required
                  type="number"
                  step="0.001"
                  value={form.new_qty}
                  onChange={(e) => set("new_qty", e.target.value)}
                  disabled={!form.batch_no}
                  className={inp}
                  data-testid="mv-new-qty"
                />
              </Field>
            ) : (
              <Field label="Quantity *">
                <input required type="number" step="0.001" value={form.qty} onChange={(e) => set("qty", e.target.value)} className={inp} data-testid="mv-qty" />
              </Field>
            )}
            <Field label="Unit Cost (₹)">
              <input type="number" step="0.01" value={form.unit_cost} onChange={(e) => set("unit_cost", e.target.value)} className={inp} data-testid="mv-unit-cost" />
            </Field>
          </div>
          {type === "in" && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Batch No">
                <input value={form.batch_no} onChange={(e) => set("batch_no", e.target.value)} className={inp} data-testid="mv-batch-no" />
              </Field>
              <Field label="Expiry Date">
                <input type="date" value={form.expiry_date} onChange={(e) => set("expiry_date", e.target.value)} className={inp} data-testid="mv-expiry" />
                {!form.expiry_date && selectedItem?.shelf_life_days > 0 && (
                  <span className="block text-[11px] text-slate-400 mt-1">
                    Leave blank to use shelf life ({selectedItem.shelf_life_days} days from today)
                  </span>
                )}
              </Field>
            </div>
          )}
          {type === "in" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Vendor *">
                  <VendorCombobox
                    vendors={vendors}
                    value={form.vendor_id}
                    onPick={pickVendor}
                    testId="mv-vendor"
                    placeholder="Select vendor…"
                  />
                </Field>
                <Field label="GRN No">
                  <input
                    readOnly
                    value={form.grn_no}
                    onChange={(e) => set("grn_no", e.target.value)}
                    placeholder="Auto-generated on save"
                    className={`${inp} bg-slate-50 text-slate-400 cursor-not-allowed`}
                    data-testid="mv-grn"
                  />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Invoice No *">
                  <input required value={form.invoice_no} onChange={(e) => set("invoice_no", e.target.value)} className={inp} data-testid="mv-invoice-no" />
                </Field>
                <Field label="Invoice Date *">
                  <input required type="date" value={form.invoice_date} onChange={(e) => set("invoice_date", e.target.value)} className={inp} data-testid="mv-invoice-date" />
                </Field>
              </div>
              <div className="block">
                <span className="block text-xs font-medium text-slate-600 mb-1">Invoice Document * (PDF or photo)</span>
                <DocumentUpload kind="invoice" value={invoiceDoc} onChange={setInvoiceDoc} label="Upload invoice" testId="mv-invoice-doc" />
              </div>
              <Field label="Storage Location">
                <input value={form.to_location} onChange={(e) => set("to_location", e.target.value)} className={inp} />
              </Field>
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="mv-qa-hold-note">
                This stock will be on <strong>QA Hold</strong> and can&apos;t be used until QA accepts it in Procurement &amp; QC → GRN / QA.
              </p>
            </>
          )}
          <Field label="Reference / Note">
            <input value={form.reference} onChange={(e) => set("reference", e.target.value)} placeholder="PO #, batch ID, recipe ID…" className={inp} data-testid="mv-ref" />
          </Field>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-md border border-slate-200 bg-white text-sm text-slate-700 hover:bg-slate-50">Cancel</button>
          <button
            type="submit"
            disabled={submitting}
            className={`h-10 px-5 rounded-md text-white text-sm font-medium ${type === "in" ? "bg-emerald-600 hover:bg-emerald-700" : type === "out" ? "bg-rose-600 hover:bg-rose-700" : "bg-amber-600 hover:bg-amber-700"} disabled:opacity-50`}
            data-testid="mv-submit"
          >
            {submitting ? "Saving…" : `Record ${type.toUpperCase()}`}
          </button>
        </div>
      </form>
    </div>
  );
}

const inp = "w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-600 mb-1">{label}</span>
      {children}
    </label>
  );
}
